import { PageHeader, PageShell } from '@/components/PageShell';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RECALLS_DEFAULT_FILTER, type RecallsFilter, recentRecallJobsState, unprocessedKindCountState } from '@/state';
import { useAsyncAtomValue } from '@/state/loadableHooks';
import { RECALL_PROCESS_KIND_LIMIT } from '@rock/types';
import { useSetAtom } from 'jotai';
import { type FC, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { describeActiveRecallJob, isRecallJobActive, PROCESS_COST_PER_RECALL_USD } from './recallJobs';
import { RecallsTable } from './RecallsTable';
import { useRecallJobRequest } from './useRecallJobRequest';

/**
 * One product kind the classifier could not match to a catalog type: what Processing it would do, and
 * every untyped recall of that kind. Its rows are all one kind and all untyped, so the list carries no
 * filters and no Types column.
 */
export const RecallKindPage: FC = () => {
    const { productKind = '' } = useParams<{ productKind: string }>();

    return (
        <PageShell>
            <PageHeader
                title={productKind}
                description="Recalls the classifier filed under this product kind without matching a catalog type."
                backLabel="Back to No Matched Type"
            />
            <ProcessCard productKind={productKind} />
            <KindRecallsCard productKind={productKind} />
        </PageShell>
    );
};

/**
 * Process the kind's still-`classified` recalls, counted from the server: the No Matched Type count also holds
 * the kind's drafts and approved ones, which Process never reaches. Recounted on arrival and when a job ends.
 */
const ProcessCard: FC<{ productKind: string }> = ({ productKind }) => {
    const { request, isRequesting, canRequest } = useRecallJobRequest();
    const jobs = useAsyncAtomValue(recentRecallJobsState, []);
    const countState = unprocessedKindCountState(productKind);
    const unprocessed = useAsyncAtomValue(countState);
    const recount = useSetAtom(countState);
    // Until a queued job finishes, its recalls still count as unprocessed, and a second job would pay for them again.
    const activeJob = jobs.find(
        (job) =>
            isRecallJobActive(job) && job.request.kind === 'process-kind' && job.request.productKind === productKind,
    );
    const activeJobId = activeJob?.id;
    useEffect(() => {
        if (!activeJobId) recount();
    }, [activeJobId, recount]);
    const batch = Math.min(unprocessed ?? 0, RECALL_PROCESS_KIND_LIMIT);
    const cost = `$${(batch * PROCESS_COST_PER_RECALL_USD).toFixed(2)}`;

    const handleProcess = () =>
        request(
            { kind: 'process-kind', productKind, limit: batch },
            `Process ${batch} “${productKind}” recall(s)`,
            (jobId) => `Job ${jobId} · about ${cost}.`,
        );

    return (
        <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-4">
                <div className="flex flex-col gap-1.5">
                    <CardTitle>Process</CardTitle>
                    <CardDescription>
                        Processing reads each notice with both extractors to pull out its matching rules (models, serial
                        ranges, dates), then dry-runs those rules against the catalog. Each recall becomes a draft in
                        the Catalog tab; nothing reaches a homeowner until a draft is approved. One click processes up
                        to {RECALL_PROCESS_KIND_LIMIT} (about ${PROCESS_COST_PER_RECALL_USD.toFixed(2)} each); larger
                        batches run from rock-gpt.
                    </CardDescription>
                </div>
                <Button
                    onClick={handleProcess}
                    disabled={batch === 0 || !!activeJob || isRequesting || !canRequest}
                    title={`Extract rules for ${batch} recall(s) · about ${cost}`}
                >
                    {isRequesting ? 'Requesting…' : `Process ${unprocessed === undefined ? '…' : batch}`}
                </Button>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">
                {activeJob ? (
                    <span className="text-amber-700">{describeActiveRecallJob(activeJob)}</span>
                ) : (
                    `${unprocessed === undefined ? '…' : unprocessed.toLocaleString()} recall(s) of this kind waiting to be processed.`
                )}
            </CardContent>
        </Card>
    );
};

const KindRecallsCard: FC<{ productKind: string }> = ({ productKind }) => {
    const filter = useMemo(
        (): RecallsFilter => ({ ...RECALLS_DEFAULT_FILTER, untypedKind: productKind }),
        [productKind],
    );
    return (
        <Card>
            <CardHeader>
                <CardTitle>Recalls</CardTitle>
                <CardDescription>
                    Newest publish first, whatever their status: processed ones show as drafts, and ones approved
                    through a topic as approved.
                </CardDescription>
            </CardHeader>
            <CardContent>
                <RecallsTable filter={filter} emptyText="No untyped recalls of this kind." hideTypes />
            </CardContent>
        </Card>
    );
};
